import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';

import { FormWrapper } from 'components/common/FormWrapper';
import {
  FormDescription,
  FormRow,
  FormTitle,
} from 'components/common/FormWrapper/styles';
import { PKDOnboardingLayout } from 'components/PKDOnboardingLayout';
import { TextInput } from 'components/PKDTextField';
import ContactInfoImage from 'images/contact-info.png';
import { PKDFilledButton } from 'components/FilledButton';
import { PKDSelect } from 'components/PKDSelect';
import { ErrorWrapper } from 'shared-components/ErrorWrapper';
import {
  addressFormatRegExp,
  phoneNumberFormatRegExp,
  zipCodeRegExp,
} from 'utils/regexp';
import { OutlineButton } from 'shared-components/Buttons/Outline';
import { isMobile } from 'utils/isMobile';
import MaskedInput from 'components/MaskedInput';
import { openSnackbar } from 'store/application';
import { useUpsertPersonAttributeMutation } from 'store/api/pkd/endpoints/person';
import {
  formatNationalPhoneNumber,
  formatPhoneNumbers,
  getPersonContactRecords,
  hasEmptyProperties,
  hasNonEmptyProperty,
  initialContactFormValues,
} from './utils';
import { isOnboardedUser } from 'routes/route.utils';
import SkeletonLoader from './skeletonLoader';
import {
  getAttributesPayload,
  getPersonAttribute,
} from 'pages/MedicalInformation/utils';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { useGetStatesQuery } from 'store/api/pkd/endpoints/country';

export const ContactInformation = () => {
  const dispatch = useDispatch();
  const {
    isPersonLoading,
    isPersonAttributesFetching,
    personId,
    auth0User,
    person,
  } = useGetPersonAndAttributes();
  
  const { states, isStatesLoading } =
    useGetStatesQuery(
      null,
      {
        selectFromResult : ({ data, isLoading }) => ({
          states          : data?.subdivisions,
          isStatesLoading : isLoading,
        }),
      }
    );

  const [upsertPersonAttribute, { isLoading: isPersonAttributeUpserting }] =
    useUpsertPersonAttributeMutation();

  const [isUpdating, setIsUpdating] = useState(false);

  const personContactRecords = getPersonContactRecords(person?.attributes);
  personContactRecords.phone = formatNationalPhoneNumber(
    personContactRecords?.phone
  );
  personContactRecords.home_phone = formatNationalPhoneNumber(
    personContactRecords?.home_phone
  );

  const defaultContactRecords = {
    ...initialContactFormValues,
    ...personContactRecords,
  };

  const {
    control,
    formState: { isValid, defaultValues: formDefaultValues },
    reset,
    handleSubmit,
    watch,
    getValues,
    trigger,
  } = useForm({
    mode          : 'onChange',
    defaultValues : defaultContactRecords,
  });

  const { postal_code, phone, home_phone, address, city, state } = getValues();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (hasNonEmptyProperty(personContactRecords)) {
      trigger();
    }
  }, [
    personContactRecords?.state,
    personContactRecords?.city,
    personContactRecords?.postal_code,
  ]);

  const onSubmit = handleSubmit(async (data) => {
    setIsUpdating(true);
    const parseData = {
      ...data,
      hasMigratedContactInfo : 'Yes',
    };
    delete parseData.yearOfBirth;
    delete parseData.monthOfBirth;
    delete parseData.dayOfBirth;
    const attributes = getPersonAttribute(
      person?.attributes,
      initialContactFormValues
    );
    const body = getAttributesPayload(
      { ...parseData, email: auth0User.email },
      attributes
    );
    const formattedAttribute = formatPhoneNumbers(body);
    if (formattedAttribute.length > 0) {
      // upsert attributes
      const { error } = await upsertPersonAttribute({
        body : formattedAttribute,
        personId,
      });
      setIsUpdating(false);
      if (error) {
        return dispatch(
          openSnackbar({
            type : 'error',
            message :
              'Could not save your contact information. Please try again',
          })
        );
      }
    }

    dispatch(
      openSnackbar({
        type    : 'success',
        message : 'Contact information successfully saved',
      })
    );
    navigate('/consent', { replace: true });
  });

  const goBack = () => navigate('/medical-information');
  const skeletonLoading =
    isPersonLoading || (isPersonAttributesFetching && !isUpdating) || isStatesLoading;
  const hasNonEmptyProp = hasNonEmptyProperty(formDefaultValues);
  const hasEmptyProps = hasEmptyProperties(formDefaultValues);

  useEffect(() => {
    if (!skeletonLoading && hasEmptyProps) {
      reset(defaultContactRecords);
    }
  }, [skeletonLoading]);

  useEffect(() => {
    if (hasNonEmptyProp && !hasEmptyProps) {
      trigger();
    }
  }, [hasNonEmptyProp, hasEmptyProps]);

  // Unauthorized navigation
  useEffect(() => {
    if (isOnboardedUser(person?.state)) {
      return navigate('/homepage', { replace: true });
    }
  }, [person?.state, person?.city, person?.postal_code]);

  return (
    <PKDOnboardingLayout>
      <FormWrapper
        image={ContactInfoImage}>
        <Box>
          <Box>
            <FormTitle>Contact information</FormTitle>
            <Box
              maxWidth='613px'>
              <FormDescription>
                We use your address to understand where our community members
                are located, and will provide opportunities to opt-in to text
                message notifications for various research activities. We do not
                share this information with anyone else, and if we would like to
                use it for something new, we will ask your permission to do
                that.
              </FormDescription>
            </Box>
            {skeletonLoading
              ? (
                <SkeletonLoader />
              )
              : (
                <>
                  <form
                    onSubmit={handleSubmit(onSubmit)}>
                    <FormRow
                      marginTop='46px'>
                      <Box>
                        <Controller
                          control={control}
                          name='phone'
                          rules={{
                            pattern : {
                              value : phoneNumberFormatRegExp,
                              message :
                              'Invalid phone number. Should be (XXX) XXX-XXXX or XXX XXX-XXXX',
                            },
                          }}
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <ErrorWrapper>
                              <TextInput
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  trigger();
                                }}
                                label='Mobile Phone (optional)'
                                fullWidth
                                inputProps={{
                                  mask        : '(#00) 000-0000',
                                  definitions : {
                                    '#' : /[1-9]/,
                                  },
                                }}
                                InputProps={{
                                  inputComponent : MaskedInput,
                                }}
                                error={error?.message && Boolean(phone)}
                                hasErrors={
                                  error &&
                                Boolean(phone) && {
                                    type    : 'custom',
                                    message : error.message,
                                  }
                                }
                              />
                            </ErrorWrapper>
                          )}
                        />
                      </Box>
                      <Box>
                        <Controller
                          control={control}
                          name='home_phone'
                          rules={{
                            pattern : {
                              value : phoneNumberFormatRegExp,
                              message :
                              'Invalid phone number. Should be (XXX) XXX-XXXX or XXX XXX-XXXX',
                            },
                          }}
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <ErrorWrapper>
                              <TextInput
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  trigger();
                                }}
                                label='Home Phone (optional)'
                                fullWidth
                                inputProps={{
                                  mask        : '(#00) 000-0000',
                                  definitions : {
                                    '#' : /[1-9]/,
                                  },
                                }}
                                InputProps={{
                                  inputComponent : MaskedInput,
                                }}
                                error={error?.message && Boolean(home_phone)}
                                hasErrors={
                                  error &&
                                Boolean(home_phone) && {
                                    type    : 'custom',
                                    message : error.message,
                                  }
                                }
                              />
                            </ErrorWrapper>
                          )}
                        />
                      </Box>
                    </FormRow>
                    <FormRow
                      marginTop='46px'>
                      <Box>
                        <Controller
                          control={control}
                          name='address'
                          rules={{
                            maxLength : {
                              value   : 50,
                              message : 'Maximum characters allowed is 50',
                            },
                            pattern : {
                              value : addressFormatRegExp,
                              message :
                              'You have invalid characters in your address',
                            },
                          }}
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <ErrorWrapper>
                              <TextInput
                                onChange={(e) => {
                                  onChange(e);
                                  trigger();
                                }}
                                value={value}
                                label='Address (optional)'
                                fullWidth
                                error={error?.message && Boolean(address)}
                                hasErrors={
                                  error &&
                                Boolean(address) && {
                                    type    : 'custom',
                                    message : error.message,
                                  }
                                }
                              />
                            </ErrorWrapper>
                          )}
                        />
                      </Box>
                      <Box>
                        <Controller
                          control={control}
                          name='state'
                          rules={{
                            required : {
                              value   : true,
                              message : 'Please select a state',
                            },
                          }}
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <ErrorWrapper>
                              <PKDSelect
                                fullWidth
                                label='State'
                                value={watch('state')}
                                onChange={(e) => {
                                  onChange(e);
                                  trigger();
                                }}
                                options={states?.map((state) => ({
                                  label : state.name,
                                  value : state.code,
                                }))}
                                error={error?.message && Boolean(state)}
                                hasErrors={
                                  error &&
                                Boolean(state) && {
                                    type    : 'custom',
                                    message : error.message,
                                  }
                                }
                              />
                            </ErrorWrapper>
                          )}
                        />
                      </Box>
                    </FormRow>
                    <FormRow
                      marginTop='46px'>
                      <Box>
                        <Controller
                          control={control}
                          name='city'
                          rules={{
                            required : {
                              value   : true,
                              message : 'This field is required',
                            },
                          }}
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <ErrorWrapper>
                              <TextInput
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  trigger();
                                }}
                                label='City'
                                fullWidth
                                error={error?.message && Boolean(city)}
                                hasErrors={
                                  error &&
                                Boolean(city) && {
                                    type    : 'custom',
                                    message : error.message,
                                  }
                                }
                              />
                            </ErrorWrapper>
                          )}
                        />
                      </Box>
                      <Box>
                        <Controller
                          control={control}
                          name='postal_code'
                          rules={{
                            required : {
                              value   : true,
                              message : 'This field is required',
                            },
                            pattern : {
                              value : zipCodeRegExp,
                              message :
                              'Invalid format. Should be XXXXX or or XXXXX-XXXX',
                            },
                          }}
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <ErrorWrapper>
                              <TextInput
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  trigger();
                                }}
                                label='Zip Code'
                                fullWidth
                                inputProps={{
                                  mask : `#0000${
                                    postal_code?.length > 4 ? '-0000' : ''
                                  }`,
                                  definitions : {
                                    '#' : /[0-9]/,
                                  },
                                }}
                                InputProps={{
                                  inputComponent : MaskedInput,
                                }}
                                error={error?.message && Boolean(postal_code)}
                                hasErrors={
                                  error &&
                                Boolean(postal_code) && {
                                    type    : 'custom',
                                    message : error.message,
                                  }
                                }
                              />
                            </ErrorWrapper>
                          )}
                        />
                      </Box>
                    </FormRow>
                  </form>
                  <Box
                    marginTop='42px' marginBottom='42px'>
                    <OutlineButton
                      type='button'
                      width={isMobile() ? '100%' : '224px'}
                      height='40px'
                      fontFamily='Montserrat'
                      color={theme.palette.primary.blue1}
                      borderColor={theme.palette.primary.blue1}
                      marginBottom={isMobile() ? '20px' : 0}
                      text='Go back'
                      onClickHandler={goBack}
                    />
                    <PKDFilledButton
                      onClick={onSubmit}
                      sx={{
                        width      : isMobile() ? '100%' : '224px',
                        marginLeft : '26px',
                      }}
                      disabled={
                        isPersonAttributeUpserting ||
                      isPersonAttributesFetching ||
                      !isValid
                      }
                      isLoading={
                        isPersonAttributeUpserting || isPersonAttributesFetching
                      }>
                    Continue
                    </PKDFilledButton>
                  </Box>
                </>
              )}
          </Box>
        </Box>
      </FormWrapper>
    </PKDOnboardingLayout>
  );
};
